import { Button } from '@components/ui/button';
import {
  type ProcessedLink,
  type ProcessedNode,
  SANKEY_FONT_SIZE_MAP,
  calculateAutoHeight,
  calculateOptimalSankeySize,
  calculateTextWidth,
  extractNodeData,
  findConnectedLinks,
  findConnectedNodes,
  generateUID,
  highlightConnectedElements,
  resetElementsOpacity,
  truncateText,
  wrapText,
} from '@utils/sankey.util';
import * as d3 from 'd3';
import { sankey, sankeyJustify, sankeyLinkHorizontal } from 'd3-sankey';
import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Base node data that can be extended with custom properties
 */
export type SankeyNodeData<T = Record<string, unknown>> = {
  /** Category for color grouping */
  category?: string;
  /** Optional unique identifier for the node (if not provided, name will be used) */
  id?: string;
  /** Unique name identifier for the node */
  name: string;
} & T;

/**
 * Base link data that can be extended with custom properties
 */
export type SankeyLinkData<T = Record<string, unknown>> = {
  /** Source node name or index */
  source: string | number;
  /** Target node name or index */
  target: string | number;
  /** Flow value between nodes */
  value: number;
} & T;

/**
 * Data structure for the Sankey chart
 */
export type SankeyData<L = Record<string, unknown>, N = Record<string, unknown>> = {
  /** Array of links between nodes */
  links: SankeyLinkData<L>[];
  /** Array of nodes in the diagram */
  nodes: SankeyNodeData<N>[];
};

/**
 * Link color configuration options
 */
export type LinkColorMode = 'source' | 'source-target' | 'static' | 'target';

/**
 * Sankey chart settings configuration
 */
export type SankeySettings = {
  /** Custom color mapping for node categories */
  colorMap?: Record<string, string>;
  /** Enable verbose debug logs to console */
  debug?: boolean;
  /** Controls how aggressively auto-height grows to resolve label/node overlaps (>=1). Higher = taller. */
  autoHeightAggressiveness?: number;
  /** Chart height in pixels, or "auto" for automatic calculation */
  height: number | 'auto';
  /** Hide node subtext (type labels). When true, layout ignores subtext size. */
  hideSubtext?: boolean;
  /** Opacity for non-hovered links during hover (0..1) */
  dimmedLinkOpacity?: number;
  /** How much of the inter-column gap a label in an inner column is allowed to use */
  innerColumnAvailableRatio?: number;
  /** Whether this is an AI-generated Sankey with unpredictable distribution (uses more generous height calculation) */
  isAiGenerated?: boolean;
  /** Font size for node labels in pixels */
  labelFontSize: number;
  /** Fraction of a column's width reserved as horizontal margin between opposing labels */
  labelMarginRatio?: number;
  /** Maximum width for labels before truncation (0 = no truncation) */
  labelMaxWidth: number;
  /** Gap between label and subtext in pixels */
  labelSubtextGap: number;
  /** How to color the links */
  linkColor: LinkColorMode;
  /** Default opacity for links */
  linkOpacity: number;
  /** Opacity for hovered links during hover (0..1) */
  hoverLinkOpacity?: number;
  /** Minimum vertical height for node rectangles (px) */
  minNodeHeight?: number;
  /** Hard cap for label margin so very wide diagrams don't waste excessive space */
  maxLabelMargin?: number;
  /** Absolute minimum label width to avoid wrapping every single character */
  minLabelWidthAbs?: number;
  /** Minimum label / sub-text width expressed as a fraction of columnWidth */
  minLabelWidthRatio?: number;
  /** Padding between nodes */
  nodePadding: number;
  /** Width of node rectangles */
  nodeWidth: number;
  /** Font size for node subtext in pixels */
  subtextFontSize: number;
  /** Maximum width for subtext before truncation (0 = no truncation) */
  subtextMaxWidth: number;
  /** Chart width in pixels */
  width: number;
  /** Vertical margin (top/bottom) inside the extent to avoid clipping */
  verticalMargin?: number;
};

/**
 * Props for the basic Sankey chart component
 */
export type SankeyProps<L = Record<string, unknown>, N = Record<string, unknown>> = {
  /** Margin to subtract from container width when using auto-width */
  autoWidthMargin?: number;
  /** Chart data containing nodes and links */
  data: SankeyData<L, N>;
  /** Function to format node labels */
  formatNodeLabel?: (node: SankeyNodeData<N>) => string;
  /** Function to format node subtext */
  formatNodeSubtext?: (node: SankeyNodeData<N>) => string | undefined;
  /** Maximum height in pixels before showing overflow controls */
  maxHeight?: number;
  /** Callback when a node is clicked */
  onNodeClick?: (node: SankeyNodeData<N>, index: number) => void;
  /** Callback when the full-screen visualization is opened */
  onOpenFullScreen?: () => void;
  /** Chart settings configuration */
  settings?: Partial<SankeySettings>;
  /** Size preset for dynamic font sizing */
  size?: 'xs' | 'sm' | 'md';
};

// =============================================================================
// CONSTANTS
// =============================================================================

// Chart defaults
export const DEFAULT_HEIGHT = 500;
export const DEFAULT_LINK_COLOR: LinkColorMode = 'source-target';
export const DEFAULT_LINK_OPACITY = 0.3;
export const DEFAULT_NODE_MARGIN = 6;
export const DEFAULT_NODE_PADDING = 12;
export const DEFAULT_NODE_WIDTH = 12;
export const DEFAULT_TRANSITION_DURATION = 100;
export const DEFAULT_WIDTH = 928;
export const DEFAULT_MIN_NODE_HEIGHT = 12; // px
export const DEFAULT_VERTICAL_MARGIN = 24; // px top/bottom padding

// Label background padding
/** Horizontal padding inside the label background rectangles */
export const RECT_PADDING_X = 2;
/** Vertical padding inside the label background rectangles */
export const RECT_PADDING_Y = 6;

// Label width and margin tuning
/** Fraction of a column's width reserved as horizontal margin between opposing labels */
export const LABEL_MARGIN_RATIO = 0.15; // 25 % of columnWidth
/** Hard cap for that margin so very wide diagrams don't waste excessive space */
export const MAX_LABEL_MARGIN = 64; // px
/** How much of the inter-column gap a label in an inner column is allowed to use */
export const INNER_COLUMN_AVAILABLE_RATIO = 0.65; // 65 % of gapWidth
/** Minimum label / sub-text width expressed as a fraction of columnWidth */
export const MIN_LABEL_WIDTH_RATIO = 0.15; // 15 % of columnWidth
/** Absolute minimum to avoid wrapping every single character */
export const MIN_LABEL_WIDTH_ABS = 20; // px

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * A basic Sankey chart component built with D3.js and d3-sankey
 *
 * This component provides a simple Sankey diagram implementation with:
 * - Configurable dimensions and styling
 * - Multiple link coloring modes
 * - Node alignment options
 * - Automatic data processing and layout
 * - Full TypeScript support with generics for custom data
 * - Automatic height calculation when height is set to "auto"
 */
export const Sankey = <L = Record<string, unknown>, N = Record<string, unknown>>(
  props: SankeyProps<L, N>,
): ReactElement => {
  const {
    data,
    formatNodeLabel,
    formatNodeSubtext,
    onOpenFullScreen,
    onNodeClick,
    settings,
    size,
    maxHeight,
    autoWidthMargin = 0,
  } = props;

  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [autoWidth, setAutoWidth] = useState<number>(DEFAULT_WIDTH);
  const lastObservedWidthRef = useRef<number | null>(null);
  const resizeRafRef = useRef<number | null>(null);

  // Auto-width detection using ResizeObserver
  useEffect(() => {
    if (!containerRef.current) return;

    if (typeof ResizeObserver === 'undefined') {
      console.warn('ResizeObserver is not supported in this browser');
      return;
    }

    const MIN_DELTA = 4; // px
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const widthAvailable = entry.contentRect.width;

        // Avoid noisy updates for tiny changes
        const prev = lastObservedWidthRef.current;
        if (prev !== null && Math.abs(widthAvailable - prev) < MIN_DELTA) {
          continue;
        }
        lastObservedWidthRef.current = widthAvailable;

        if (resizeRafRef.current !== null) {
          cancelAnimationFrame(resizeRafRef.current);
        }
        resizeRafRef.current = requestAnimationFrame(() => {
          setAutoWidth(widthAvailable - autoWidthMargin);
        });
      }
    });

    observer.observe(containerRef.current);
    return () => {
      observer.disconnect();
      if (resizeRafRef.current !== null) cancelAnimationFrame(resizeRafRef.current);
    };
  }, [autoWidthMargin]);

  useEffect(() => {
    if (!svgRef.current || !data.nodes.length || !data.links.length) {
      return;
    }

    const {
      colorMap,
      autoHeightAggressiveness: settingsAutoHeightAggressiveness,
      debug: debugEnabled = false,
      height: heightSetting = DEFAULT_HEIGHT,
      innerColumnAvailableRatio = INNER_COLUMN_AVAILABLE_RATIO,
      isAiGenerated = false,
      labelMaxWidth = 0,
      labelMarginRatio = LABEL_MARGIN_RATIO,
      linkColor = DEFAULT_LINK_COLOR,
      hideSubtext = false,
      linkOpacity = DEFAULT_LINK_OPACITY,
      maxLabelMargin = MAX_LABEL_MARGIN,
      minLabelWidthAbs = MIN_LABEL_WIDTH_ABS,
      minLabelWidthRatio = MIN_LABEL_WIDTH_RATIO,
      nodePadding = DEFAULT_NODE_PADDING,
      nodeWidth = DEFAULT_NODE_WIDTH,
      subtextMaxWidth = 0,
      width: settingsWidth = DEFAULT_WIDTH,
      labelFontSize: settingsLabelFontSize,
      subtextFontSize: settingsSubtextFontSize,
      labelSubtextGap: settingsLabelSubtextGap,
      hoverLinkOpacity: settingsHoverLinkOpacity,
      dimmedLinkOpacity: settingsDimmedLinkOpacity,
      minNodeHeight: settingsMinNodeHeight,
      verticalMargin: settingsVerticalMargin,
    } = settings || {};

    const width = autoWidth > 0 ? autoWidth : settingsWidth;
    const verticalMargin = settingsVerticalMargin ?? DEFAULT_VERTICAL_MARGIN;
    const minNodeHeight = settingsMinNodeHeight ?? DEFAULT_MIN_NODE_HEIGHT;

    // For auto height, use the calculated height
    // We'll dynamically adjust later if actual content exceeds this
    const height = heightSetting === 'auto' ? calculateAutoHeight(data, nodePadding, isAiGenerated) : heightSetting;

    // Clear previous content
    d3.select(svgRef.current).selectAll('*').remove();

    // Create SVG selection
    const svg = d3
      .select(svgRef.current)
      .attr('width', width)
      .attr('height', height)
      .attr('viewBox', [0, 0, width, height])
      .attr('style', 'max-width: 100%; height: auto;');

    // Pre-compute deterministic category (type) order using a topological sort
    // over the category graph inferred from links. This guarantees left-to-right
    // placement that matches the data flow, independent of d3-sankey internals.
    const computeCategoryOrder = (nodes: SankeyNodeData<N>[], links: SankeyLinkData<L>[]) => {
      const order = new Map<string, number>();
      const categories = new Set<string>();

      // Collect all categories present in nodes
      nodes.forEach((node) => {
        const category = node.category || 'default';
        categories.add(category);
      });

      // Fast lookup for nodes by id/name
      const nodeIdMap = new Map<string, SankeyNodeData<N>>();
      nodes.forEach((node) => {
        const nodeId = node.id || node.name;
        nodeIdMap.set(nodeId, node);
      });

      // Build directed graph between categories
      const graph = new Map<string, Set<string>>();
      const inDegree = new Map<string, number>();
      categories.forEach((cat) => {
        graph.set(cat, new Set());
        inDegree.set(cat, 0);
      });

      links.forEach((link) => {
        const sourceNode = nodeIdMap.get(String(link.source));
        const targetNode = nodeIdMap.get(String(link.target));
        if (sourceNode && targetNode) {
          const sourceCat = sourceNode.category || 'default';
          const targetCat = targetNode.category || 'default';
          if (sourceCat !== targetCat && !graph.get(sourceCat)?.has(targetCat)) {
            graph.get(sourceCat)?.add(targetCat);
            inDegree.set(targetCat, (inDegree.get(targetCat) || 0) + 1);
          }
        }
      });

      // Kahn's algorithm (topological sort)
      const sorted: string[] = [];
      const queue: string[] = [];
      inDegree.forEach((deg, cat) => {
        if (deg === 0) queue.push(cat);
      });
      while (queue.length > 0) {
        const current = queue.shift()!;
        sorted.push(current);
        graph.get(current)?.forEach((neighbor) => {
          const next = (inDegree.get(neighbor) || 0) - 1;
          inDegree.set(neighbor, next);
          if (next === 0) queue.push(neighbor);
        });
      }

      // Include any remaining categories (disconnected or cycles) preserving discovery order
      categories.forEach((cat) => {
        if (!sorted.includes(cat)) sorted.push(cat);
      });

      sorted.forEach((cat, index) => order.set(cat, index));
      return order;
    };

    const categoryOrder = computeCategoryOrder(data.nodes, data.links);

    // Assign initial depths to nodes based on the computed category order
    const nodesWithDepth = data.nodes.map((node) => {
      const category = node.category || 'default';
      const depth = categoryOrder.get(category) ?? 0;
      return { ...node, depth };
    });

    // Configure the Sankey generator
    // CRITICAL: nodeId must use the full ID (type:name pattern) to support duplicate names across types
    // For example: "domain:Infrastructure" vs "use:Infrastructure"
    const sankeyGenerator = sankey<SankeyNodeData<N>, SankeyLinkData<L>>()
      .nodeId((d) => d.id || d.name)
      .nodeAlign(sankeyJustify)
      .nodeWidth(nodeWidth)
      .nodePadding(nodePadding)
      .extent([
        [1, verticalMargin],
        [width - 1, height - verticalMargin],
      ]);

    // Process the data - create copies to avoid mutation, using nodes with assigned depths
    const graphData = {
      nodes: nodesWithDepth.map((d) => Object.assign({}, d)),
      links: data.links.map((d) => Object.assign({}, d)),
    };

    const { nodes, links } = sankeyGenerator(graphData);

    // Type assertion for processed data - d3-sankey transforms the data
    let processedNodes = nodes as ProcessedNode<N>[];
    let processedLinks = links as ProcessedLink<L>[];

    // Build a map for fast node lookup by ID (using type:name pattern)
    const originalNodeMap = new Map<string, SankeyNodeData<N>>();
    data.nodes.forEach((node) => {
      const nodeId = node.id || node.name;
      originalNodeMap.set(nodeId, node);
    });

    // Verify and enforce category-based column separation
    // Use the deterministic category order computed earlier
    const categoryDepthMap = new Map<string, number>(categoryOrder);

    // Fix depths: ensure nodes of the same category have the same depth
    processedNodes.forEach((node) => {
      const nodeId = node.id || node.name;
      const nodeData = originalNodeMap.get(nodeId);
      const category = nodeData?.category || 'default';
      const assignedDepth = categoryDepthMap.get(category) ?? 0;

      // Override the depth if d3-sankey changed it
      if (node.depth !== assignedDepth) {
        node.depth = assignedDepth;
      }
    });

    // Fix overlapping nodes in the same column
    // Group nodes by depth (column)
    const nodesByDepth = new Map<number, ProcessedNode<N>[]>();
    processedNodes.forEach((node) => {
      const depth = node.depth ?? 0;
      if (!nodesByDepth.has(depth)) {
        nodesByDepth.set(depth, []);
      }
      nodesByDepth.get(depth)!.push(node);
    });

    // Sort nodes in each column by y position (initial positioning from d3-sankey)
    nodesByDepth.forEach((nodesInColumn) => {
      nodesInColumn.sort((a, b) => (a.y0 ?? 0) - (b.y0 ?? 0));
    });

    // Manually spread the computed layers so that the left-most column starts at
    // x = 0 and the right-most column finishes at x = width - nodeWidth. This
    // ensures the diagram always occupies the entire allotted horizontal space
    // while still keeping nodes grouped in their respective layer/column.

    // Determine the deepest layer produced by the Sankey generator.
    let maxDepth = Math.max(...processedNodes.map((n) => n.depth ?? 0));

    // Divide the available width (minus the width of a node rectangle) evenly
    // across the number of *gaps* between layers (maxDepth). For a single-layer
    // diagram we avoid a divide-by-zero by falling back to 1.
    let columnWidth = (width - nodeWidth) / Math.max(1, maxDepth);

    processedNodes.forEach((node) => {
      node.x0 = (node.depth ?? 0) * columnWidth;
      node.x1 = node.x0 + nodeWidth;
    });

    // Ensure no vertical overlap within each column by reflowing nodes sequentially
    {
      // Use the final SVG height (after possible auto-height relayout), fallback to planned height
      const effectiveHeightAttr = svgRef.current?.getAttribute('height');
      const effectiveHeight = effectiveHeightAttr ? Number(effectiveHeightAttr) : height;
      const originalNodePos = new Map<ProcessedNode<unknown>, { y0: number; y1: number }>();
      processedNodes.forEach((n) => {
        originalNodePos.set(n as unknown as ProcessedNode<unknown>, { y0: n.y0 ?? 0, y1: n.y1 ?? 0 });
      });
      const byDepthReflow = new Map<number, ProcessedNode<N>[]>();
      processedNodes.forEach((n) => {
        const d = n.depth ?? 0;
        if (!byDepthReflow.has(d)) byDepthReflow.set(d, []);
        byDepthReflow.get(d)!.push(n);
      });
      byDepthReflow.forEach((nodesInColumn) => {
        nodesInColumn.sort((a, b) => (a.y0 ?? 0) - (b.y0 ?? 0));
        // Compute total node height and distribute remaining space evenly between gaps
        const availableSpan = Math.max(0, effectiveHeight - verticalMargin * 2);
        const totalNodesHeight = nodesInColumn.reduce((sum, n) => sum + Math.max(0, (n.y1 ?? 0) - (n.y0 ?? 0)), 0);
        const gaps = Math.max(1, nodesInColumn.length - 1);
        const extraSpace = Math.max(0, availableSpan - totalNodesHeight);
        const evenGap = extraSpace / gaps;
        const gapPadding = Math.max(nodePadding, evenGap);
        let currentY = verticalMargin;
        for (const n of nodesInColumn) {
          const span = Math.max(0, (n.y1 ?? 0) - (n.y0 ?? 0));
          n.y0 = currentY;
          n.y1 = currentY + span;
          currentY = n.y1 + gapPadding;
        }
      });
      // Reposition link offsets to match new node positions (preserve relative offsets)
      processedLinks.forEach((l) => {
        const sOrig = originalNodePos.get(l.source as unknown as ProcessedNode<unknown>);
        const tOrig = originalNodePos.get(l.target as unknown as ProcessedNode<unknown>);
        if (sOrig) {
          const sSpanOrig = Math.max(1, sOrig.y1 - sOrig.y0);
          const sSpanNew = Math.max(1, (l.source.y1 ?? 0) - (l.source.y0 ?? 0));
          const sT = ((l.y0 ?? sOrig.y0) - sOrig.y0) / sSpanOrig;
          l.y0 = (l.source.y0 ?? 0) + sT * sSpanNew;
        }
        if (tOrig) {
          const tSpanOrig = Math.max(1, tOrig.y1 - tOrig.y0);
          const tSpanNew = Math.max(1, (l.target.y1 ?? 0) - (l.target.y0 ?? 0));
          const tT = ((l.y1 ?? tOrig.y0) - tOrig.y0) / tSpanOrig;
          l.y1 = (l.target.y0 ?? 0) + tT * tSpanNew;
        }
      });
    }

    // Render all nodes and links (no per-column limit)

    // --------------------------------------------------------------------
    // Automatically choose optimal size based on data and available space
    // --------------------------------------------------------------------

    const optimalSize = size || calculateOptimalSankeySize(data, columnWidth, formatNodeLabel, formatNodeSubtext);

    const labelFontSize = settingsLabelFontSize ?? SANKEY_FONT_SIZE_MAP[optimalSize].labelFontSize;
    const subtextFontSize = settingsSubtextFontSize ?? SANKEY_FONT_SIZE_MAP[optimalSize].subtextFontSize;
    const labelSubtextGap = settingsLabelSubtextGap ?? SANKEY_FONT_SIZE_MAP[optimalSize].labelSubtextGap;

    // Create color scale for categories
    const color = colorMap
      ? (category: string) => colorMap[category] || d3.schemeCategory10[0]
      : d3.scaleOrdinal(d3.schemeCategory10);

    // ====================================================================
    // LABEL METRICS AND REQUIRED HEIGHT ESTIMATION (NO OFFSETS)
    // ====================================================================

    // Helper to estimate label/subtext heights so we know the bounding box
    const estimateLabelMetrics = (node: ProcessedNode<N>) => {
      const labelText = formatNodeLabel?.(node) ?? node.name;
      const subtextText = hideSubtext ? undefined : (formatNodeSubtext?.(node) ?? undefined);

      // Text layout constants (keep in sync with rendering section below)
      const rectPaddingX = RECT_PADDING_X;
      const rectPaddingY = RECT_PADDING_Y;
      const textMargin = Math.min(maxLabelMargin, columnWidth * labelMarginRatio);

      // Calculate available width for wrapping/truncation (same as later)
      // For inner columns we restrict the width to half of the inter-column gap so
      // that two labels facing each other cannot overlap horizontally.
      const gapWidth = columnWidth - nodeWidth - textMargin - rectPaddingX * 2;
      const isEdgeColumn = (node.depth ?? 0) === 0 || (node.depth ?? 0) === maxDepth;

      const minDynamicWidth = Math.max(minLabelWidthAbs, columnWidth * minLabelWidthRatio);

      const availableWidth = Math.max(minDynamicWidth, isEdgeColumn ? gapWidth : gapWidth * innerColumnAvailableRatio);

      const processedLabelText = truncateText({
        text: labelText,
        maxWidth: Math.min(labelMaxWidth === 0 ? Math.max(80, availableWidth * 0.9) : labelMaxWidth, availableWidth),
        fontSize: labelFontSize,
        fontWeight: '600',
      });

      const labelLines = wrapText({
        text: processedLabelText,
        maxWidth: availableWidth,
        fontSize: labelFontSize,
        fontWeight: '600',
      });

      let totalTextHeight = (labelLines.length - 1) * labelFontSize; // main label multi-line height

      if (subtextText) {
        const processedSubtextText = truncateText({
          text: subtextText,
          maxWidth: Math.min(
            subtextMaxWidth === 0 ? Math.max(80, availableWidth * 1) : subtextMaxWidth,
            availableWidth,
          ),
          fontSize: subtextFontSize,
          fontWeight: '400',
        });

        const subtextLines = wrapText({
          text: processedSubtextText,
          maxWidth: availableWidth,
          fontSize: subtextFontSize,
          fontWeight: '400',
        });

        totalTextHeight += labelSubtextGap + (subtextLines.length - 1) * subtextFontSize;
      }

      return {
        totalTextHeight,
        rectPaddingY,
      };
    };
    // Build a depth map for overlap checks
    const depthMap = new Map<number, ProcessedNode<N>[]>();
    processedNodes.forEach((node) => {
      const depth = node.depth ?? 0;
      if (!depthMap.has(depth)) depthMap.set(depth, []);
      depthMap.get(depth)!.push(node);
    });

    // Compute the maximum required height multiplier to keep centered labels from overlapping
    let requiredHeightByOverlap = height;
    let maxLabelBottomPre = 0;
    let minLabelTopPre = Infinity;

    depthMap.forEach((nodesInColumn) => {
      nodesInColumn.sort((a, b) => (a.y0 ?? 0) - (b.y0 ?? 0));

      for (let i = 0; i < nodesInColumn.length; i++) {
        const node = nodesInColumn[i];
        const { totalTextHeight, rectPaddingY } = estimateLabelMetrics(node);
        const center = ((node.y0 ?? 0) + (node.y1 ?? 0)) / 2;
        const half = totalTextHeight / 2 + rectPaddingY;
        const top = center - half;
        const bottom = center + half;
        minLabelTopPre = Math.min(minLabelTopPre, top);
        maxLabelBottomPre = Math.max(maxLabelBottomPre, bottom);

        if (i > 0) {
          const prev = nodesInColumn[i - 1];
          const { totalTextHeight: prevH, rectPaddingY: prevPad } = estimateLabelMetrics(prev);
          const prevCenter = ((prev.y0 ?? 0) + (prev.y1 ?? 0)) / 2;
          const requiredDelta = prevH / 2 + prevPad + nodePadding + (totalTextHeight / 2 + rectPaddingY);
          const safeActualDelta = Math.max(center - prevCenter, 4);
          if (requiredDelta > safeActualDelta) {
            // Scale height proportionally, but guard against near-zero spacing
            const ratio = requiredDelta / safeActualDelta;
            requiredHeightByOverlap = Math.max(requiredHeightByOverlap, height * ratio);
          }
        }
      }
    });

    // If auto-height and labels require more room, re-run layout with a larger height so nodes spread
    if (heightSetting === 'auto') {
      const neededByBounds = Math.max(height, maxLabelBottomPre + verticalMargin, -minLabelTopPre + height);

      // Ensure there is enough height to accommodate minimum node height in the
      // densest column: count * minNodeHeight + padding * (count - 1) + margins
      let densestCount = 0;
      depthMap.forEach((nodesInColumn) => {
        densestCount = Math.max(densestCount, nodesInColumn.length);
      });
      const nodesMinRequired =
        densestCount > 0
          ? densestCount * minNodeHeight + Math.max(0, densestCount - 1) * nodePadding + verticalMargin * 2
          : 0;

      // Estimate minimum height to accommodate label blocks across densest column
      let maxLabelBlockHeight = 0;
      processedNodes.forEach((n) => {
        const m = estimateLabelMetrics(n);
        maxLabelBlockHeight = Math.max(maxLabelBlockHeight, m.totalTextHeight + m.rectPaddingY * 2);
      });
      const labelsMinRequired =
        densestCount > 0
          ? densestCount * maxLabelBlockHeight + Math.max(0, densestCount - 1) * nodePadding + verticalMargin * 2
          : 0;

      // Clamp overlap-driven height to avoid runaway growth on crowded columns
      const heightAgg = Math.max(1, settingsAutoHeightAggressiveness ?? 3);
      const clampedOverlap = Math.min(height * heightAgg, requiredHeightByOverlap);

      const nextHeight = Math.ceil(Math.max(neededByBounds, nodesMinRequired, labelsMinRequired, clampedOverlap));

      if (nextHeight > height) {
        const sankeyGenerator2 = sankey<SankeyNodeData<N>, SankeyLinkData<L>>()
          .nodeId((d) => d.id || d.name)
          .nodeAlign(sankeyJustify)
          .nodeWidth(nodeWidth)
          .nodePadding(nodePadding)
          .extent([
            [1, verticalMargin],
            [width - 1, nextHeight - verticalMargin],
          ]);

        const graphData2 = {
          nodes: nodesWithDepth.map((d) => Object.assign({}, d)),
          links: data.links.map((d) => Object.assign({}, d)),
        };

        const relayout = sankeyGenerator2(graphData2);
        processedNodes = relayout.nodes as ProcessedNode<N>[];
        processedLinks = relayout.links as ProcessedLink<L>[];

        // Fix depths again
        const originalNodeMap2 = new Map<string, SankeyNodeData<N>>();
        data.nodes.forEach((node) => {
          const nodeId = node.id || node.name;
          originalNodeMap2.set(nodeId, node);
        });
        processedNodes.forEach((node) => {
          const nodeId = node.id || node.name;
          const nodeData = originalNodeMap2.get(nodeId);
          const category = nodeData?.category || 'default';
          const assignedDepth = categoryDepthMap.get(category) ?? 0;
          if (node.depth !== assignedDepth) node.depth = assignedDepth;
        });

        // Spread columns again
        maxDepth = Math.max(...processedNodes.map((n) => n.depth ?? 0));
        columnWidth = (width - nodeWidth) / Math.max(1, maxDepth);
        processedNodes.forEach((node) => {
          node.x0 = (node.depth ?? 0) * columnWidth;
          node.x1 = node.x0 + nodeWidth;
        });

        // Update SVG dimensions
        svg.attr('height', nextHeight).attr('viewBox', [0, 0, width, nextHeight]);
      }
    }

    // IMPORTANT: Do not post-scale link widths or re-stack nodes here.
    // d3-sankey already guarantees each node's height equals the sum of its
    // connecting link widths (up to floating-point precision). Any post-layout
    // mutation can reintroduce mismatches. We keep the generator's output intact
    // to preserve this invariant.

    /**
     * LINKS (the connecting lines)
     */
    const linkGroup = svg
      .append('g')
      .attr('class', 'sankey-links')
      .attr('fill', 'none')
      .attr('stroke-opacity', linkOpacity)
      .selectAll<SVGGElement, ProcessedLink<L>>('g')
      .data(processedLinks)
      .join('g');

    // Performance gate: avoid blend-mode on very large link sets
    const HEAVY_LINK_THRESHOLD = 800;
    const isHeavy = processedLinks.length > HEAVY_LINK_THRESHOLD;
    if (!isHeavy) {
      linkGroup.style('mix-blend-mode', 'multiply');
    }

    /**
     * NODES
     */
    const nodeGroups = svg
      .append('g')
      .attr('class', 'sankey-nodes')
      .selectAll<SVGGElement, ProcessedNode<N>>('g')
      .data(processedNodes)
      .join('g')
      .attr('class', 'sankey-node')
      .style('cursor', onNodeClick ? 'pointer' : 'default')
      .on('mouseenter', (_event, d) => {
        highlightConnectedElements({
          svg,
          connectedLinks: findConnectedLinks(d),
          connectedNodes: findConnectedNodes(d),
          linkOpacity,
          dimOthers: true,
          dimOpacity: settingsDimmedLinkOpacity,
          highlightOpacity: settingsHoverLinkOpacity,
        });
      })
      .on('mouseleave', () => resetElementsOpacity(svg, linkOpacity));

    /* Create rectangles for nodes */
    nodeGroups
      .append('rect')
      .attr('x', (d) => d.x0 ?? 0)
      .attr('y', (d) => d.y0 ?? 0)
      .attr('height', (d) => (d.y1 ?? 0) - (d.y0 ?? 0))
      .attr('width', (d) => (d.x1 ?? 0) - (d.x0 ?? 0))
      .attr('fill', (d) => color(d.category || d.name));

    // Create gradients for source-target coloring
    // Use gradients only when not heavy; otherwise fallback to simple coloring
    const effectiveLinkColor: LinkColorMode = linkColor === 'source-target' && isHeavy ? 'source' : linkColor;

    if (effectiveLinkColor === 'source-target') {
      const defs = svg.append('defs');

      linkGroup.each(function (d) {
        const gradientId = generateUID('link-gradient');
        // Store the gradient ID on the datum for later reference
        (d as ProcessedLink<L> & { uid: string }).uid = gradientId;

        const gradient = defs
          .append('linearGradient')
          .attr('id', gradientId)
          .attr('gradientUnits', 'userSpaceOnUse')
          .attr('x1', d.source.x1 ?? 0)
          .attr('x2', d.target.x0 ?? 0);

        gradient
          .append('stop')
          .attr('offset', '0%')
          .attr('stop-color', color(d.source.category || d.source.name));

        gradient
          .append('stop')
          .attr('offset', '100%')
          .attr('stop-color', color(d.target.category || d.target.name));
      });
    }

    // Create paths for links
    const linkPaths = linkGroup
      .append('path')
      .attr('d', sankeyLinkHorizontal())
      .attr('stroke-width', (d) => d.width ?? 0)
      .style('cursor', 'pointer')
      .style('pointer-events', 'stroke')
      .on('mouseenter', (_event, d) => {
        /* Get connected links from both source and target nodes */
        const sourceConnectedLinks = findConnectedLinks(d.source);
        const targetConnectedLinks = findConnectedLinks(d.target);
        const allConnectedLinks = [...new Set([...sourceConnectedLinks, ...targetConnectedLinks])];

        /* Get connected nodes from both source and target nodes */
        const sourceConnectedNodes = findConnectedNodes(d.source);
        const targetConnectedNodes = findConnectedNodes(d.target);
        const allConnectedNodes = [...new Set([...sourceConnectedNodes, ...targetConnectedNodes])];

        highlightConnectedElements({
          svg,
          connectedLinks: allConnectedLinks,
          connectedNodes: allConnectedNodes,
          linkOpacity,
          dimOthers: true,
          dimOpacity: settingsDimmedLinkOpacity,
          highlightOpacity: settingsHoverLinkOpacity,
        });
      })
      .on('mouseleave', () => resetElementsOpacity(svg, linkOpacity));

    // Apply link colors based on mode
    if (effectiveLinkColor === 'source-target') {
      linkPaths.attr('stroke', (d) => {
        const linkWithUid = d as ProcessedLink<L> & { uid?: string };
        return linkWithUid.uid ? `url(#${linkWithUid.uid})` : '#aaa';
      });
    } else if (effectiveLinkColor === 'source') {
      linkPaths.attr('stroke', (d) => color(d.source.category || d.source.name));
    } else if (effectiveLinkColor === 'target') {
      linkPaths.attr('stroke', (d) => color(d.target.category || d.target.name));
    } else {
      linkPaths.attr('stroke', '#aaa');
    }

    /**
     * LINK TOOLTIPS
     */
    linkGroup.append('title').text((d) => {
      const sourceLabel = formatNodeLabel?.(extractNodeData<N>(d.source)) ?? d.source.name;
      const targetLabel = formatNodeLabel?.(extractNodeData<N>(d.target)) ?? d.target.name;
      return `${sourceLabel} → ${targetLabel}`;
    });

    /**
     * NODE LABELS
     */
    nodeGroups.each(function (d, _i) {
      const node = d3.select(this);
      const nodeData = d;

      // Get label text
      const labelText = formatNodeLabel?.(nodeData) ?? nodeData.name;
      const subtextText = hideSubtext ? undefined : (formatNodeSubtext?.(nodeData) ?? undefined);

      // Add tooltip to the node using the formatted label text
      node.select('rect').append('title').text(labelText);

      const nodeX =
        (nodeData.x0 ?? 0) < width / 2
          ? (nodeData.x1 ?? 0) + DEFAULT_NODE_MARGIN
          : (nodeData.x0 ?? 0) - DEFAULT_NODE_MARGIN;
      const nodeY = ((nodeData.y1 ?? 0) + (nodeData.y0 ?? 0)) / 2;
      const textAnchor = (nodeData.x0 ?? 0) < width / 2 ? 'start' : 'end';

      // -----------------------------------------------------------
      // Calculate available space for text wrapping so that labels
      // never trespass into the neighbouring column. We use the same
      // columnWidth that was calculated earlier when laying out the
      // nodes. A small gap (6 px) plus the rectangle's horizontal
      // padding (rectPaddingX × 2) is subtracted from the remainder
      // of the column after the node rectangle.
      // -----------------------------------------------------------

      const rectPaddingX = RECT_PADDING_X;
      const textMargin = Math.min(maxLabelMargin, columnWidth * labelMarginRatio);

      const gapWidth = columnWidth - nodeWidth - textMargin - rectPaddingX * 2;
      const isEdgeColumn = (nodeData.depth ?? 0) === 0 || (nodeData.depth ?? 0) === maxDepth;

      const minDynamicWidth = Math.max(minLabelWidthAbs, columnWidth * minLabelWidthRatio);

      const availableWidth = Math.max(minDynamicWidth, isEdgeColumn ? gapWidth : gapWidth * innerColumnAvailableRatio);

      // Create a parent group for text elements so we can control them (hover, click, etc.)
      const textGroup = node
        .append('g')
        .attr('class', 'sankey-node-labels')
        .style('pointer-events', 'all')
        .style('cursor', onNodeClick ? 'pointer' : 'default')
        .on('click', () => {
          if (onNodeClick) {
            const nodeIndex = processedNodes.indexOf(nodeData);
            onNodeClick(nodeData, nodeIndex);
          }
        });

      // Add tooltip to the text group showing full label and subtext content
      const tooltipContent = subtextText ? `${labelText}\n${subtextText}` : labelText;
      textGroup.append('title').text(tooltipContent);

      // ----------------------------------------------------------------------------
      // Calculate effective maximum widths for label and subtext.
      //   • When a manual max width (labelMaxWidth or subtextMaxWidth) is provided,
      //     we still clamp it to the "availableWidth" so that text never trespasses
      //     into the neighbouring column – this prevents overlap when the diagram
      //     is rendered in a narrower container or when auto-width kicks in.
      //   • When the max width is "0" (auto mode) we derive it from availableWidth
      //     just like before, but still respect the same upper bound.
      // ----------------------------------------------------------------------------

      const autoLabelMaxWidthRaw = labelMaxWidth === 0 ? Math.max(80, availableWidth * 0.9) : labelMaxWidth;
      const autoSubtextMaxWidthRaw = subtextMaxWidth === 0 ? Math.max(80, availableWidth * 1) : subtextMaxWidth;

      const effectiveLabelMaxWidth = Math.min(autoLabelMaxWidthRaw, availableWidth);
      const effectiveSubtextMaxWidth = Math.min(autoSubtextMaxWidthRaw, availableWidth);

      // Apply truncation using either auto-calculated or manual width
      const processedLabelText = truncateText({
        text: labelText,
        maxWidth: effectiveLabelMaxWidth,
        fontSize: labelFontSize,
        fontWeight: '600',
      });

      // Wrap the processed label text using the available width
      const labelLines = wrapText({
        text: processedLabelText,
        maxWidth: availableWidth,
        fontSize: labelFontSize,
        fontWeight: '600',
      });
      const actualMainLabelHeight = (labelLines.length - 1) * labelFontSize; // font size per line

      // Calculate actual text widths (post-wrapping)
      const labelTextWidth = calculateTextWidth(labelLines, labelFontSize, '600');
      let subtextTextWidth = 0;
      let subtextLines: string[] = [];

      if (!hideSubtext && subtextText) {
        // Apply truncation using either auto-calculated or manual width
        const processedSubtextText = truncateText({
          text: subtextText,
          maxWidth: effectiveSubtextMaxWidth,
          fontSize: subtextFontSize,
          fontWeight: '400',
        });

        // Wrap subtext
        subtextLines = wrapText({
          text: processedSubtextText,
          maxWidth: availableWidth,
          fontSize: subtextFontSize,
          fontWeight: '400',
        });
        subtextTextWidth = calculateTextWidth(subtextLines, subtextFontSize, '400');
      }

      // Use the maximum width of label and subtext so the background box fits both
      const actualTextWidth = Math.max(labelTextWidth, subtextTextWidth);

      // Gap between main label and subtext: use configurable value
      const totalTextHeight = actualMainLabelHeight + (!hideSubtext && subtextText ? labelSubtextGap : 0);

      // Labels strictly centered on nodes (no vertical offsets). We ensure
      // enough height earlier so this does not overlap.
      const startY = nodeY - totalTextHeight / 2;

      // Background rectangle dimensions (horizontal padding already defined earlier as rectPaddingX)
      const rectPaddingY = RECT_PADDING_Y;
      const rectWidth = actualTextWidth + rectPaddingX * 2;
      const rectHeight = totalTextHeight + rectPaddingY * 2;
      const rectX = textAnchor === 'start' ? nodeX - rectPaddingX : nodeX - rectWidth + rectPaddingX;
      const rectY = startY - rectPaddingY;

      // Add background rectangle for better text visibility
      textGroup
        .append('rect')
        .attr('x', rectX)
        .attr('y', rectY)
        .attr('width', rectWidth)
        .attr('height', rectHeight)
        .attr('fill', 'white')
        .attr('fill-opacity', 0.3)
        .attr('rx', 4)
        .attr('ry', 4);

      // Add main label text lines
      const mainTextElement = textGroup
        .append('text')
        .attr('x', nodeX)
        .attr('y', startY)
        .attr('text-anchor', textAnchor)
        .style('font-size', `${labelFontSize}px`)
        .style('fill', '#333')
        .style('font-weight', '500');

      labelLines.forEach((line, index) => {
        mainTextElement
          .append('tspan')
          .attr('x', nodeX)
          .attr('dy', index === 0 ? '0.30em' : '1.2em')
          .text(line);
      });

      // Add subtext if provided
      if (!hideSubtext && subtextText && subtextLines.length > 0) {
        const subtextElement = textGroup
          .append('text')
          .attr('x', nodeX)
          .attr('y', startY + actualMainLabelHeight + labelSubtextGap)
          .attr('text-anchor', textAnchor)
          .attr('class', 'text-gray-500')
          .style('font-size', `${subtextFontSize}px`)
          .style('fill', 'currentColor')
          .style('font-weight', '400');

        subtextLines.forEach((line, index) => {
          subtextElement
            .append('tspan')
            .attr('x', nodeX)
            .attr('dy', index === 0 ? '0.25em' : '1.2em')
            .text(line);
        });
      }
    });

    // Final summary of label positions and height usage
    const allLabelBottoms: number[] = [];
    nodeGroups.each(function (d) {
      const labelText = formatNodeLabel?.(d) ?? d.name;
      const subtextText = hideSubtext ? undefined : (formatNodeSubtext?.(d) ?? undefined);
      const nodeY = ((d.y1 ?? 0) + (d.y0 ?? 0)) / 2;
      // Rough estimate of label bounds (same calculation as above)
      const { totalTextHeight } = estimateLabelMetrics(d);
      const rectPaddingY = RECT_PADDING_Y;
      const startY = nodeY - totalTextHeight / 2;
      const labelBottom = startY + totalTextHeight + rectPaddingY * 2;
      allLabelBottoms.push(labelBottom);
    });

    const maxLabelBottom = Math.max(...allLabelBottoms, 0);
    const maxNodeY = Math.max(...processedNodes.map((n) => n.y1 ?? 0));
    const actualRequiredHeight = Math.max(maxNodeY, maxLabelBottom) + 20; // +20 for safety margin

    // If auto-height mode and content exceeds calculated height, adjust dynamically
    if (heightSetting === 'auto' && actualRequiredHeight > height) {
      // Update SVG height to fit actual content
      svg.attr('height', actualRequiredHeight).attr('viewBox', [0, 0, width, actualRequiredHeight]);
    }

    // (min-height enforcement is applied before rendering above)
  }, [data, formatNodeLabel, formatNodeSubtext, onNodeClick, settings, size, autoWidth]);

  // Calculate if content overflows the max height
  const calculatedHeight = useMemo(() => {
    const heightSetting = settings?.height ?? DEFAULT_HEIGHT;
    const isAiGenerated = settings?.isAiGenerated ?? false;
    return heightSetting === 'auto'
      ? calculateAutoHeight(data, settings?.nodePadding ?? DEFAULT_NODE_PADDING, isAiGenerated)
      : heightSetting;
  }, [data, settings?.height, settings?.nodePadding, settings?.isAiGenerated]);

  const isOverflowing = maxHeight !== undefined && calculatedHeight > maxHeight;

  return (
    <div
      ref={containerRef}
      className="w-full relative"
    >
      <div
        className="relative"
        style={{
          maxHeight: maxHeight ? `${maxHeight}px` : undefined,
          overflow: maxHeight ? 'hidden' : 'visible',
        }}
      >
        <svg
          ref={svgRef}
          className="max-w-full h-auto"
        />
        {isOverflowing && (
          <div
            className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none"
            style={{
              background:
                'linear-gradient(to bottom, transparent 0%, rgba(255, 255, 255, 0.65) 50%, rgba(255, 255, 255, 1) 100%)',
            }}
          />
        )}
      </div>
      {isOverflowing && (
        <div className="-mt-2 z-10 relative text-center py-8 px-6 ">
          <div className="text-sm font-medium text-gray-700 mb-1">Data Too Large to Render</div>
          {onOpenFullScreen && (
            <Button
              variant="link"
              className="text-sm text-[#3D2EFF] font-medium mx-auto"
              onClick={onOpenFullScreen}
            >
              Open Full-screen Visualization
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
